import { Link } from 'react-router-dom';
import {
  MapPinIcon,
  UsersIcon,
  RectangleStackIcon,
  CloudArrowDownIcon,
  ChevronRightIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector } from '../store/hooks';
import Card from '../components/Card';

interface SettingItem {
  name: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

const settingsItems: SettingItem[] = [
  {
    name: 'Locations',
    description: 'Manage building locations and facilities',
    href: '/settings/locations',
    icon: MapPinIcon,
  },
  {
    name: 'Key Types',
    description: 'Configure key types and categories',
    href: '/settings/keytypes',
    icon: RectangleStackIcon,
  },
  {
    name: 'Users',
    description: 'Manage user accounts and permissions',
    href: '/settings/users',
    icon: UsersIcon,
    adminOnly: true,
  },
  {
    name: 'Notifications',
    description: 'Configure email notifications and alerts',
    href: '/settings/notifications',
    icon: BellIcon,
    adminOnly: true,
  },
  {
    name: 'Backup & Restore',
    description: 'Export and restore the entire database',
    href: '/settings/backup',
    icon: CloudArrowDownIcon,
    adminOnly: true,
  },
];

export default function Settings() {
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const isAdmin = currentUser?.role === 'admin';

  const visibleItems = settingsItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Settings</h1>
        <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
          Manage application settings and configuration
        </p>
      </div>

      <Card>
        <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
          {visibleItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="flex items-center justify-between py-4 px-2 -mx-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors group first:pt-0 last:pb-0"
            >
              <div className="flex items-center">
                <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg mr-4">
                  <item.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                    {item.name}
                  </h3>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">{item.description}</p>
                </div>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-secondary-400 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
            </Link>
          ))}
        </div>
      </Card>

      {!isAdmin && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Some settings are only available to administrators.
          </p>
        </div>
      )}
    </div>
  );
}
